import { useState, useEffect } from "react";

import { AddIcon, CloseIcon } from "@chakra-ui/icons";
import {
  VStack,
  HStack,
  Box,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  IconButton,
  Divider,
  SimpleGrid,
} from "@chakra-ui/react";

import { useConstants } from "./constants";
import { fetchMachineTypes, type MachineType } from "./machineTypes";

type Service = {
  id: number;
  name: string;
  cpu: number;
  memory: number;
  gpu: number;
  replicas: number;
};

type Props = {
  onCostChange?: (cost: number, services: Service[]) => void;
  theme?: "light" | "dark";
};

export default function ResourceCalculator({ onCostChange, theme = "light" }: Props) {
  const [machineTypes, setMachineTypes] = useState<MachineType[]>([]);
  const { constants, isLoading: constantsLoading, error: constantsError } = useConstants();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function loadMachineTypes() {
      try {
        const types = await fetchMachineTypes();
        setMachineTypes(types);
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to load machine types"));
        setIsLoading(false);
      }
    }
    loadMachineTypes();
  }, []);

  // Get GPU machine specs
  const gpuMachine = machineTypes.find(spec => spec.gpu > 0);

  const [services, setServices] = useState<Service[]>([
    { id: 1, name: "Service 1", cpu: 1, memory: 1, gpu: 0, replicas: 1 },
  ]);
  const [nextId, setNextId] = useState(2);

  const addService = () => {
    setServices([
      ...services,
      {
        id: nextId,
        name: `Service ${nextId}`,
        cpu: 1,
        memory: 1,
        gpu: 0,
        replicas: 1,
      },
    ]);
    setNextId(nextId + 1);
  };

  const removeService = (id: number) => {
    setServices(services.filter(s => s.id !== id));
  };

  const handleServiceChange = (
    id: number,
    field: keyof Omit<Service, "id">, // Remove "name" from the Omit
    value: number | string // Allow string for name field
  ) => {
    setServices(prevServices =>
      prevServices.map(service => {
        if (service.id !== id) return service;

        // Handle name field separately since it's a string
        if (field === "name") {
          return { ...service, name: value as string };
        }

        // Number fields continue as before
        const numericValue = Number(value);

        // Enforce minimum values
        const minValues = {
          cpu: 1,
          memory: 1,
          gpu: 0,
          replicas: 1,
        };

        // Create updated service with new value
        const updatedService = { ...service, [field]: numericValue };

        // If this service uses GPU, limit CPU and Memory to GPU machine specs
        if (updatedService.gpu > 0 && gpuMachine) {
          return {
            ...updatedService,
            cpu:
              field === "cpu"
                ? Math.min(Math.max(numericValue, minValues.cpu), gpuMachine.cpus)
                : Math.min(service.cpu, gpuMachine.cpus),
            memory:
              field === "memory"
                ? Math.min(Math.max(numericValue, minValues.memory), gpuMachine.memory)
                : Math.min(service.memory, gpuMachine.memory),
          };
        }

        // For non-GPU services, use regular MAX values
        const maxValues = {
          cpu: constants?.MAX_CPU || Infinity,
          memory: constants?.MAX_MEMORY || Infinity,
          gpu: gpuMachine?.gpu || Infinity,
          replicas: Infinity,
        };

        const validatedValue = Math.min(Math.max(numericValue, minValues[field]), maxValues[field]);

        return { ...service, [field]: validatedValue };
      })
    );
  };

  const calculateServiceCost = (service: Service, standalone = true) => {
    if (!constants) return 0;

    const singleInstanceCost =
      service.gpu > 0
        ? constants.GPU_PRICE
        : Math.max(service.cpu / constants.MAX_CPU, service.memory / constants.MAX_MEMORY) *
          constants.BASE_PRICE;

    return singleInstanceCost * service.replicas;
  };

  const calculateCost = () => {
    const total = services.reduce((total, service) => total + calculateServiceCost(service), 0);
    onCostChange?.(total, services);
    return total;
  };

  // Add a warning message when a service has GPU
  const getGpuWarning = (service: Service) => {
    if (service.gpu > 0) {
      return (
        <Text color="orange.500" fontSize="xs" mt={1}>
          Note: GPU services require dedicated machines and will be charged the full machine price
        </Text>
      );
    }
    return null;
  };

  // Get theme-specific colors
  const colors = {
    text: theme === "dark" ? "white" : "gray.700",
    subtext: theme === "dark" ? "gray.400" : "gray.600",
    border: theme === "dark" ? "whiteAlpha.200" : "gray.200",
    bg: theme === "dark" ? "transparent" : "white",
    hover: theme === "dark" ? "whiteAlpha.100" : "gray.50",
    divider: theme === "dark" ? "whiteAlpha.200" : "gray.200",
  };

  if (isLoading || constantsLoading) {
    return <Text>Loading machine specifications...</Text>;
  }

  if (error || constantsError) {
    return (
      <Text color="red.500">
        Error loading machine specifications: {error?.message || constantsError?.message}
      </Text>
    );
  }

  if (!gpuMachine || !constants) {
    return <Text color="red.500">Error: Machine specifications not found</Text>;
  }

  return (
    <VStack spacing={6} w="full">
      <Text color={colors.text} fontSize="sm">
        Add your services and their resource requirements to estimate hourly cost. GPU services require
        dedicated machines.
      </Text>

      {services.map(service => (
        <Box
          key={service.id}
          bg={colors.bg}
          borderColor={colors.border}
          borderRadius="md"
          borderWidth="1px"
          p={4}
          w="full"
        >
          <VStack spacing={4} w="full">
            <HStack justify="space-between" w="full">
              <FormControl flex="1">
                <FormLabel color={colors.text}>Service Name</FormLabel>
                <Input
                  color={colors.text}
                  placeholder="e.g., Web Server"
                  value={service.name}
                  onChange={e => handleServiceChange(service.id, "name", e.target.value)}
                />
              </FormControl>
              <IconButton
                _hover={{ bg: colors.hover }}
                aria-label="Remove service"
                color={colors.subtext}
                icon={<CloseIcon />}
                size="sm"
                variant="ghost"
                onClick={() => removeService(service.id)}
              />
            </HStack>

            <VStack align="stretch" spacing={4} w="full">
              <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} w="full">
                <FormControl>
                  <FormLabel color={colors.text} minH="24px">
                    CPU Cores
                  </FormLabel>
                  <NumberInput
                    max={constants.MAX_CPU}
                    min={0}
                    value={service.cpu}
                    onChange={value => handleServiceChange(service.id, "cpu", Number(value))}
                  >
                    <NumberInputField bg={theme === "dark" ? "whiteAlpha.50" : "white"} color={colors.text} />
                    <NumberInputStepper>
                      <NumberIncrementStepper color={colors.text} />
                      <NumberDecrementStepper color={colors.text} />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel color={colors.text} minH="24px">
                    Memory (GB)
                  </FormLabel>
                  <NumberInput
                    max={constants.MAX_MEMORY}
                    min={0}
                    value={service.memory}
                    onChange={value => handleServiceChange(service.id, "memory", Number(value))}
                  >
                    <NumberInputField bg={theme === "dark" ? "whiteAlpha.50" : "white"} color={colors.text} />
                    <NumberInputStepper>
                      <NumberIncrementStepper color={colors.text} />
                      <NumberDecrementStepper color={colors.text} />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel color={colors.text} minH="24px">
                    GPU Cores
                  </FormLabel>
                  <NumberInput
                    max={gpuMachine?.gpu}
                    min={0}
                    value={service.gpu}
                    onChange={value => handleServiceChange(service.id, "gpu", Number(value))}
                  >
                    <NumberInputField bg={theme === "dark" ? "whiteAlpha.50" : "white"} color={colors.text} />
                    <NumberInputStepper>
                      <NumberIncrementStepper color={colors.text} />
                      <NumberDecrementStepper color={colors.text} />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel color={colors.text} minH="24px">
                    Replicas
                  </FormLabel>
                  <NumberInput
                    min={1}
                    value={service.replicas}
                    onChange={value => handleServiceChange(service.id, "replicas", Number(value))}
                  >
                    <NumberInputField bg={theme === "dark" ? "whiteAlpha.50" : "white"} color={colors.text} />
                    <NumberInputStepper>
                      <NumberIncrementStepper color={colors.text} />
                      <NumberDecrementStepper color={colors.text} />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </SimpleGrid>
            </VStack>

            <HStack justify="space-between" pt={2} w="full">
              <VStack align="start" spacing={0}>
                {getGpuWarning(service)}
                <Text color={colors.subtext} fontSize="sm">
                  {service.gpu > 0
                    ? `Using ${service.replicas} dedicated GPU machine${service.replicas > 1 ? "s" : ""}`
                    : `Using ${Math.max(
                        (service.cpu / constants.MAX_CPU) * 100,
                        (service.memory / constants.MAX_MEMORY) * 100
                      ).toFixed(1)}% of CPU/Memory machine × ${service.replicas} replica${
                        service.replicas > 1 ? "s" : ""
                      }`}
                </Text>
              </VStack>
              <Text color={colors.text} fontSize="sm" fontWeight="medium">
                ${calculateServiceCost(service).toFixed(2)}/hour
              </Text>
            </HStack>
          </VStack>
        </Box>
      ))}

      <Button
        _hover={{
          bg: theme === "dark" ? "whiteAlpha.100" : colors.hover,
          borderColor: theme === "dark" ? "white" : colors.border,
        }}
        bg="transparent"
        borderColor={theme === "dark" ? "whiteAlpha.400" : colors.border}
        color={theme === "dark" ? "white" : colors.text}
        leftIcon={<AddIcon />}
        size="sm"
        variant="outline"
        onClick={addService}
      >
        Add Service
      </Button>

      <Divider borderColor={colors.divider} />

      <VStack spacing={2} w="full">
        {services.map(service => (
          <HStack key={service.id} fontSize="sm" justify="space-between" w="full">
            <Text color={colors.text}>{service.name}:</Text>
            <Text color={colors.text}>${calculateServiceCost(service).toFixed(2)}/hour</Text>
          </HStack>
        ))}
        <Divider />
        <HStack justify="space-between" w="full">
          <Text color={colors.text} fontWeight="medium">
            Total Estimated Cost:
          </Text>
          <Text color={colors.text} fontSize="lg" fontWeight="bold">
            ${calculateCost().toFixed(2)}/hour
          </Text>
        </HStack>
      </VStack>
    </VStack>
  );
}
